import React, { useState } from 'react';
import { Row, Col, Space } from 'antd';
import { I18n } from '@aws-amplify/core';

import { GhostButton } from '@/components/ui/Button';
import { useApplicationContext } from '@/context/v1/Application/context';
import { ExtractionModelDetailsModal } from './ExtractionModelDetailsModal';
import { useDeleteDataExtractionModel } from '@/hooks/useDeleteDataExtractionModel';
import { useGetDataExtractionModelList } from '@/hooks/useGetDataExtractionModelList';
import { BaseModal } from '@/components/Settings/DataExtraction/Modals/shared/BaseModal';
import { DeleteDataExtractionModal } from '@/components/Settings/DataExtraction/Modals';
import { Container, Divider, Title, DeleteButton, CustomCol, CustomRow } from './styles';
import * as S from '@/components/Settings/DataExtraction/styles';

interface ModelProps {
	visible?: boolean;
	onCancel: () => void;
}

export function Model({ visible = false, onCancel }: Readonly<ModelProps>) {
	const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
	const [deleteModelId, setDeleteModelId] = useState<string | null>(null);
	const [deleteModelName, setDeleteModelName] = useState<string | null>(null);
	const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);

	const { organization, company } = useApplicationContext();
	const { isLoading: isDeletingLoading, mutate: deleteModel } = useDeleteDataExtractionModel();

	const { data: models } = useGetDataExtractionModelList({
		organization_id: organization?.id,
		company_id: company?.id
	});

	const selectedModel = models?.rows.find((m) => m.id === selectedModelId);

	function handleRowClick(modelId: string): void {
		setSelectedModelId(modelId);
		setIsEditModalOpen(true);
	}

	function handleCloseEditModal(): void {
		setIsEditModalOpen(false);
		setSelectedModelId(null);
	}

	function handleAskDelete(modelId: string, modelName: string): void {
		setDeleteModelId(modelId);
		setDeleteModelName(modelName);
	}

	function handleConfirmDelete(): void {
		if (deleteModelId) {
			deleteModel({
				organization_id: organization?.id,
				company_id: company?.id,
				data_extraction_model_id: deleteModelId
			});
			setDeleteModelId(null);
		}
	}

	function handleCancelDelete(): void {
		setDeleteModelId(null);
	}

	const Footer = (): JSX.Element => (
		<Row justify="center">
			<Col>
				<Space direction="horizontal" size={16}>
					<GhostButton onClick={onCancel}>{I18n.get('Cancel')}</GhostButton>
				</Space>
			</Col>
		</Row>
	);

	return (
		<>
			<BaseModal
				title={<Title>{I18n.get('Choose model')}</Title>}
				visible={visible}
				onCancel={onCancel}
				width={700}
				footer={<Footer />}
			>
				<Space direction="vertical" size={32} style={{ width: '100%' }}>
					<Row align="middle" justify="space-between" style={{ width: '100%' }}>
						<S.LabelLargeRegular>
							{I18n.get(
								'Select the desired model and edit the necessary information to create a new extraction'
							)}
						</S.LabelLargeRegular>
					</Row>
					<Row>
						<Space direction="vertical" size={16} style={{ width: '100%' }}>
							{models?.rows.map((model) => (
								<Container
									key={model.id}
									onClick={() => handleRowClick(model.id)}
									style={{ cursor: 'pointer' }}
								>
									<CustomRow style={{ width: '100%' }}>
										<CustomCol xs={24} sm={6} md={5} lg={4}>
											<S.LabelMediumSemibold>{model.model_name}</S.LabelMediumSemibold>
										</CustomCol>
										<Divider />
										<CustomCol xs={24} sm={5} md={4} lg={4}>
											<S.LabelSmallRegular>
												{model.report_name ? model.report_name : '-'}
											</S.LabelSmallRegular>
										</CustomCol>
										<Divider />
										<CustomCol xs={24} sm={2} md={3} lg={5}>
											<S.LabelSmallRegular>
												{I18n.get('Tools')}: {model.tools_count}
											</S.LabelSmallRegular>
										</CustomCol>
										<Divider />
										<CustomCol xs={24} sm={2} md={3} lg={5}>
											<S.LabelSmallRegular>
												{I18n.get('Action plan')}:{' '}
												{model.has_action_plan_extraction ? I18n.get('Yes') : I18n.get('No')}
											</S.LabelSmallRegular>
										</CustomCol>
										<Divider />
										<CustomCol xs={24} sm={2} md={2} lg={1}>
											<DeleteButton
												onClick={(e) => {
													e.stopPropagation();
													handleAskDelete(model.id, model.model_name);
												}}
											/>
										</CustomCol>
									</CustomRow>
								</Container>
							))}
						</Space>
					</Row>
				</Space>
			</BaseModal>
			{selectedModelId && selectedModel && (
				<ExtractionModelDetailsModal
					visible={isEditModalOpen}
					onCancel={handleCloseEditModal}
					modelId={selectedModelId}
					organizationId={organization?.id}
					companyId={company?.id}
				/>
			)}
			<DeleteDataExtractionModal
				visible={!!deleteModelId}
				loading={isDeletingLoading}
				onCancel={handleCancelDelete}
				onConfirm={handleConfirmDelete}
				customMessage={
					<S.LabelMediumRegular>
						{`${I18n.get('Do you want to delete the extraction model')} "${deleteModelName}"?`}
					</S.LabelMediumRegular>
				}
			/>
		</>
	);
}
